import { DOCUMENT } from '@angular/common';
import { Injectable, computed, inject, signal } from '@angular/core';
import { meetsContrastAgainstAll } from 'app/core/utils/contrast';
import { OrgContextService } from 'app/core/services/org-context.service';
import { TenantContextService } from 'app/core/services/tenant-context.service';

/**
 * Platform default theming tokens — docs/COLOR_SYSTEM.md §1 (PhotoFlow Navy).
 * Applied whenever there is no tenant, no branding configured, or a
 * configured color fails the contrast guard. Kept in code (not CSS) so the
 * contrast guard can evaluate the actual fallback value it will apply.
 */
export const PLATFORM_DEFAULT_PRIMARY_COLOR = '#0047AB';
export const PLATFORM_DEFAULT_ACCENT_COLOR = '#0047AB';
// Color logo (not the white `PhotoFlow_light.png`): the app scheme is hardcoded
// `light`, so the classy shell renders this on a light `bg-card` topbar where a
// white mark is invisible. The full-color mark reads on both that light topbar
// and the dark (zinc-900) navigation rail.
export const PLATFORM_DEFAULT_LOGO_URL = 'images/branding/PhotoFlow_512px.png';
export const PLATFORM_DEFAULT_FAVICON_URL = '/images/branding/favicon.ico';

/**
 * Both platform "grounds" a tenant accent/primary color might render text or
 * an icon against — `--pf-bg` and `--pf-surface`, light theme (see
 * docs/COLOR_SYSTEM.md for the brand/status palette this guard protects).
 * Fuse's `scheme` is hardcoded `'light'` in `app.config.ts`
 * today (no live dark-mode toggle for the app shell), so the light-theme
 * pair is what actually renders; a color that fails contrast against either
 * is rejected rather than applied. (If a runtime scheme toggle is added
 * later, this list is where the dark-theme grounds join it.)
 */
const PLATFORM_GROUNDS = ['#F8FAFC', '#FFFFFF'] as const;

/** The CSS custom properties this service owns, and the DOM elements it touches. */
const CSS_VAR_PRIMARY = '--pf-primary';
const CSS_VAR_ACCENT = '--pf-accent';
const FAVICON_LINK_SELECTOR = 'link[rel="icon"]';

/**
 * Applies per-tenant theming tokens — T3.10 (root-app-multi-tenant plan,
 * Phase 3, "Per-tenant theming"; palette source of truth is
 * docs/COLOR_SYSTEM.md). Per that spec:
 *
 * - Root app hosts ALWAYS use platform branding (personal workspace has no
 *   org to brand with) — this service only overrides on a tenant host.
 * - Only accent + primary + logo + favicon are tenant-controlled; ground/ink
 *   stay platform-owned so contrast and semantics never break app-wide.
 * - A configured `PrimaryColor`/`AccentColor` is used only if it passes the
 *   WCAG AA (4.5:1) contrast guard against BOTH platform grounds; otherwise
 *   the platform default is applied instead — never a value that could
 *   render illegible text.
 *
 * Triggers its own domain resolution (`OrgContextService.resolveFromDomain`)
 * rather than assuming something else in the bootstrap path already did —
 * today nothing does (see `TenantContextService`'s doc comment: it composes
 * `OrgContextService`'s stream but never calls `resolveFromDomain` itself).
 * Instantiated eagerly via `provideEnvironmentInitializer` in `app.config.ts`
 * so theming applies before the shell renders, not after first paint.
 */
@Injectable({ providedIn: 'root' })
export class TenantThemeService {
    private readonly _document = inject(DOCUMENT);
    private readonly _orgContextService = inject(OrgContextService);
    private readonly _tenantContextService = inject(TenantContextService);

    private readonly _logoUrl = signal<string>(PLATFORM_DEFAULT_LOGO_URL);
    private readonly _appliedAccentColor = signal<string>(
        PLATFORM_DEFAULT_ACCENT_COLOR
    );

    /** Logo to render in the shell — org logo on a confirmed tenant host, platform default everywhere else. */
    readonly logoUrl = computed(() => this._logoUrl());

    /** The accent color actually in effect (post contrast-guard) — mainly for tests/inspection, not a rendering dependency (consumers should read the CSS var). */
    readonly appliedAccentColor = computed(() => this._appliedAccentColor());

    constructor() {
        // Root/unknown hosts: nothing to resolve, platform branding stands.
        // (approach.md D8 / ui-design.md §2: "Root app always uses platform
        // branding.") Still kick off resolution on tenant hosts so branding
        // is available as soon as the server confirms the org.
        if (this._tenantContextService.mode === 'tenant') {
            void this._orgContextService.resolveFromDomain();
        }

        this._orgContextService.orgContext$.subscribe((orgContext) => {
            const isConfirmedTenant =
                this._tenantContextService.mode === 'tenant' &&
                orgContext?.orgSlug === this._tenantContextService.hostnameSlug;

            if (!isConfirmedTenant || !orgContext) {
                this._applyDefaults();
                return;
            }

            this._applyBranding(orgContext);
        });
    }

    /** Resets every tenant-owned token/asset to its platform default. */
    private _applyDefaults(): void {
        this._setCssVar(CSS_VAR_PRIMARY, PLATFORM_DEFAULT_PRIMARY_COLOR);
        this._setCssVar(CSS_VAR_ACCENT, PLATFORM_DEFAULT_ACCENT_COLOR);
        this._appliedAccentColor.set(PLATFORM_DEFAULT_ACCENT_COLOR);
        this._logoUrl.set(PLATFORM_DEFAULT_LOGO_URL);
        this._setFavicon(PLATFORM_DEFAULT_FAVICON_URL);
    }

    private _applyBranding(orgContext: {
        primaryColor?: string | null;
        accentColor?: string | null;
        logoUrl?: string | null;
        faviconUrl?: string | null;
    }): void {
        const primary = this._guardedColor(
            orgContext.primaryColor,
            PLATFORM_DEFAULT_PRIMARY_COLOR
        );
        const accent = this._guardedColor(
            orgContext.accentColor,
            PLATFORM_DEFAULT_ACCENT_COLOR
        );

        this._setCssVar(CSS_VAR_PRIMARY, primary);
        this._setCssVar(CSS_VAR_ACCENT, accent);
        this._appliedAccentColor.set(accent);

        this._logoUrl.set(orgContext.logoUrl?.trim() || PLATFORM_DEFAULT_LOGO_URL);
        this._setFavicon(orgContext.faviconUrl?.trim() || PLATFORM_DEFAULT_FAVICON_URL);
    }

    /**
     * The contrast guard: an org-supplied color is used only if it meets
     * WCAG AA (4.5:1) against every platform ground; otherwise the platform
     * default is returned instead. Delegates to the standalone
     * `resolveTenantColor` below so the guard logic has exactly one
     * implementation, testable with or without the Angular injector.
     */
    private _guardedColor(
        candidate: string | null | undefined,
        fallback: string
    ): string {
        return resolveTenantColor(candidate, fallback);
    }

    private _setCssVar(name: string, value: string): void {
        this._document.documentElement.style.setProperty(name, value);
    }

    private _setFavicon(href: string): void {
        const links =
            this._document.querySelectorAll<HTMLLinkElement>(
                FAVICON_LINK_SELECTOR
            );
        links.forEach((link) => (link.href = href));
    }
}

/**
 * The contrast guard itself, as a standalone function: applies `candidate`
 * only if it meets WCAG AA (4.5:1) against every `PLATFORM_GROUNDS` entry,
 * otherwise returns `fallback`. Exported so `tenant-theme.service.spec.ts`
 * can assert on guard decisions directly, without instantiating the Angular
 * service (which needs `DOCUMENT`/`OrgContextService`/`TenantContextService`
 * DI). `TenantThemeService` delegates to this exact function.
 */
export function resolveTenantColor(
    candidate: string | null | undefined,
    fallback: string
): string {
    return meetsContrastAgainstAll(candidate, PLATFORM_GROUNDS)
        ? (candidate as string)
        : fallback;
}
